import { action, computed, makeObservable, observable } from 'mobx';

import { MetaGamesEventType } from '../../dto/metagames.types';
import type { MetagamesCurrentEvent, MetagamesResponse } from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureManagerDialogType } from '../feature-manager/types';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import { type ClaimRouletteAnimationState } from './types';

export class RouletteStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public rouletteEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setRouletteEvent(event: MetagamesCurrentEvent | undefined) {
    this.rouletteEvent = event;
  }

  @observable
  public currentOpenRouletteEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenRouletteEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenRouletteEvent = event;
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(event);
  }

  public startDialogs(type?: FeatureManagerDialogType): Promise<boolean> | boolean {
    if (type === 'Roulette') {
      void this.startRouletteGame();
      return true;
    }

    return false;
  }

  @action
  private updateOptInStatus() {
    if (this.rouletteEvent) {
      const metaCoinType = this.rootStore.coinTypeStore.coinType.toLocaleLowerCase() as 'gc' | 'sc';
      const current = this.rouletteEvent[metaCoinType];
      const updated = {
        ...this.rouletteEvent,
        [metaCoinType]: {
          ...current,
          isOptedIn: true,
        },
      };
      this.setRouletteEvent(updated);
    }
  }

  public async acknowledgeOptIn() {
    try {
      const { coinType } = this.rootStore.coinTypeStore;
      const eventId = this.rouletteEvent?.id;
      void this.updateOptInStatus();
      await postRequest<{ data: MetagamesResponse }>('metagame-events/opt-in', {
        coinType,
        eventId,
      });
    } catch (error) {
      devError('RouletteStore: acknowledgeOptIn error', error);
    }
  }

  @computed
  public get eventByCoin() {
    if (!this.rouletteEvent) {
      return undefined;
    }
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;
    return this.rouletteEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];
  }

  public async startRouletteGame() {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;

    try {
      let metagamesUrl: string | undefined;

      if (!this.rouletteEvent) {
        devError('RouletteStore: startRouletteGame error: roulette Event not found');
        return;
      }

      const eventByCoin = this.rouletteEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];

      if (!eventByCoin) {
        devError('RouletteStore: startRouletteGame error: event not found');
        return;
      }

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: currentCoinType,
        gameId: eventByCoin.gameId,
      });

      if (!launchGameResponse.data?.data) {
        devError('RouletteStore: startRouletteGame error: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameResponse.data.data;

      if (metagames) {
        const { token, path } = metagames;
        const { gameUiFlag } = this.rootStore.gameStore;

        metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          version: gameUiFlag ? '2' : '1',
          metagamesToken: gameUiFlag ? token : undefined,
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: currentCoinType,
          flags: JSON.stringify([
            { name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) },
          ]),
        });

        const metagamesEventToSet = {
          ...this.rouletteEvent,
          token,
          path,
          url: metagamesUrl,
          startDate: this.rouletteEvent.startDate,
          endDate: this.rouletteEvent.endDate,
        };

        this.setCurrentOpenRouletteEvent(metagamesEventToSet);

        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenMetagamesDialog,
          options: { type: MetaGamesEventType.ROULETTE },
        });
      } else {
        devError('RouletteStore: startRouletteGame error: metagames not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('RouletteStore: startRouletteGame error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  public closeGame(dialogType?: FeatureManagerDialogType) {
    this.setCurrentOpenRouletteEvent(undefined);
    this.rootStore.metagamesStore.onMetagameDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();

    if (dialogType) {
      this.rootStore.featureManagerStore.onDialogFinished(dialogType);
    }
  }

  @observable
  public claimRouletteAnimationState: ClaimRouletteAnimationState = {
    rouletteAmount: 0,
    enable: false,
  };

  @action
  public setClaimRouletteAnimationState(state: Partial<ClaimRouletteAnimationState>) {
    this.claimRouletteAnimationState = { ...this.claimRouletteAnimationState, ...state };
  }

  public startClaimRouletteAnimation({
    rouletteAmount,
    onAnimationEnd,
    coinType,
  }: {
    rouletteAmount: number;
    onAnimationEnd?: () => Promise<void> | void;
    coinType?: CoinType;
  }) {
    this.setClaimRouletteAnimationState({
      rouletteAmount,
      enable: true,
      coinType,
      onAnimationEnd: () => {
        this.setClaimRouletteAnimationState({ enable: false });
        void onAnimationEnd?.();
      },
    });
  }

  public sendAnalyticsEvent(analyticsParams: {
    event_action: 'click' | 'impression';
    triggered_by?: 'click' | 'lobby';
    click_location?: 'widget' | 'in_game_widget' | 'close';
  }) {
    this.rootStore.metagamesStore.sendAnalyticsEvent({
      ...analyticsParams,
      click_location: analyticsParams.click_location ?? 'widget',
      feature_sub_type: 'roulette',
    });
  }
}
